import { supabase } from '../lib/supabase';

/**
 * Message Service
 * Handles negotiation messages with real-time updates
 */
export const messageService = {
  /**
   * Get all negotiations for a user
   */
  async getUserNegotiations(userId) {
    try {
      const { data, error } = await supabase?.from('negotiations')?.select(`
          *,
          wish:voucher_wishes(
            id,
            voucher:vouchers(id, title)
          ),
          initiator:user_profiles!negotiations_initiator_id_fkey(id, full_name, avatar_url),
          responder:user_profiles!negotiations_responder_id_fkey(id, full_name, avatar_url)
        `)?.or(`initiator_id.eq.${userId},responder_id.eq.${userId}`)?.order('updated_at', { ascending: false });

      if (error) throw error;

      return data?.map(negotiation => ({
        id: negotiation?.id,
        status: negotiation?.status,
        initiatorId: negotiation?.initiator_id,
        responderId: negotiation?.responder_id,
        wishId: negotiation?.wish_id,
        createdAt: negotiation?.created_at,
        updatedAt: negotiation?.updated_at,
        initiator: {
          id: negotiation?.initiator?.id,
          fullName: negotiation?.initiator?.full_name,
          avatarUrl: negotiation?.initiator?.avatar_url
        },
        responder: {
          id: negotiation?.responder?.id,
          fullName: negotiation?.responder?.full_name,
          avatarUrl: negotiation?.responder?.avatar_url
        },
        wish: negotiation?.wish ? {
          id: negotiation?.wish?.id,
          voucher: negotiation?.wish?.voucher ? {
            id: negotiation?.wish?.voucher?.id,
            title: negotiation?.wish?.voucher?.title
          } : null
        } : null
      }));
    } catch (error) {
      console.error('Error fetching negotiations:', error);
      throw error;
    }
  },

  /**
   * Get messages for a specific negotiation
   */
  async getNegotiationMessages(negotiationId) {
    try {
      const { data, error } = await supabase?.from('negotiation_messages')?.select(`
          *,
          sender:user_profiles(id, full_name, avatar_url)
        `)?.eq('negotiation_id', negotiationId)?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(message => ({
        id: message?.id,
        messageContent: message?.message_content,
        messageType: message?.message_type,
        isRead: message?.is_read,
        senderId: message?.sender_id,
        negotiationId: message?.negotiation_id,
        offerDetails: message?.offer_details,
        createdAt: message?.created_at,
        sender: {
          id: message?.sender?.id,
          fullName: message?.sender?.full_name,
          avatarUrl: message?.sender?.avatar_url
        }
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  /**
   * Send a message in a negotiation
   */
  async sendMessage(negotiationId, messageContent, messageType = 'text', offerDetails = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('negotiation_messages')?.insert({
          negotiation_id: negotiationId,
          sender_id: user?.id,
          message_content: messageContent,
          message_type: messageType,
          offer_details: offerDetails
        })?.select(`
          *,
          sender:user_profiles(id, full_name, avatar_url)
        `)?.single();

      if (error) throw error;

      // Clear typing indicator after sending message
      await this.setTypingStatus(negotiationId, false);

      return {
        id: data?.id,
        messageContent: data?.message_content,
        messageType: data?.message_type,
        isRead: data?.is_read,
        readAt: data?.read_at,
        senderId: data?.sender_id,
        negotiationId: data?.negotiation_id,
        offerDetails: data?.offer_details,
        createdAt: data?.created_at,
        sender: {
          id: data?.sender?.id,
          fullName: data?.sender?.full_name,
          avatarUrl: data?.sender?.avatar_url
        }
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * Mark messages as read with read_at timestamp
   */
  async markMessagesAsRead(negotiationId, userId) {
    try {
      const { error } = await supabase?.from('negotiation_messages')?.update({ 
          is_read: true,
          read_at: new Date()?.toISOString()
        })?.eq('negotiation_id', negotiationId)?.neq('sender_id', userId)?.eq('is_read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  },

  /**
   * Get unread message count for a user
   */
  async getUnreadMessageCount(userId) {
    try {
      const { count, error } = await supabase?.from('negotiation_messages')?.select('*', { count: 'exact', head: true })?.neq('sender_id', userId)?.eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  /**
   * Search messages
   */
  async searchMessages(userId, searchTerm) {
    try {
      const { data, error } = await supabase?.from('negotiation_messages')?.select(`
          *,
          negotiation:negotiations(
            id,
            initiator_id,
            responder_id,
            wish:voucher_wishes(
              voucher:vouchers(id, title)
            )
          ),
          sender:user_profiles(id, full_name, avatar_url)
        `)?.ilike('message_content', `%${searchTerm}%`)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Filter to only include messages from user's negotiations
      const filtered = data?.filter(msg => 
        msg?.negotiation?.initiator_id === userId || 
        msg?.negotiation?.responder_id === userId
      );

      return filtered?.map(message => ({
        id: message?.id,
        messageContent: message?.message_content,
        messageType: message?.message_type,
        isRead: message?.is_read,
        senderId: message?.sender_id,
        negotiationId: message?.negotiation_id,
        createdAt: message?.created_at,
        sender: {
          id: message?.sender?.id,
          fullName: message?.sender?.full_name,
          avatarUrl: message?.sender?.avatar_url
        },
        negotiation: {
          id: message?.negotiation?.id,
          voucherTitle: message?.negotiation?.wish?.voucher?.title || 'Nepoznat vaučer'
        }
      }));
    } catch (error) {
      console.error('Error searching messages:', error);
      throw error;
    }
  },

  /**
   * Set typing status for a user in a negotiation
   */
  async setTypingStatus(negotiationId, isTyping) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (isTyping) {
        // Upsert typing indicator
        const { error } = await supabase?.from('typing_indicators')?.upsert({
            negotiation_id: negotiationId,
            user_id: user?.id,
            is_typing: true,
            updated_at: new Date()?.toISOString()
          }, {
            onConflict: 'negotiation_id,user_id'
          });

        if (error) throw error;
      } else {
        // Remove typing indicator
        const { error } = await supabase?.from('typing_indicators')?.delete()?.eq('negotiation_id', negotiationId)?.eq('user_id', user?.id);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error setting typing status:', error);
      throw error;
    }
  },

  /**
   * Get typing status for a negotiation
   */
  async getTypingStatus(negotiationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('typing_indicators')?.select(`
          *,
          user:user_profiles(id, full_name)
        `)?.eq('negotiation_id', negotiationId)?.neq('user_id', user?.id)?.eq('is_typing', true)?.gte('updated_at', new Date(Date.now() - 10000)?.toISOString());

      if (error) throw error;

      return data?.map(indicator => ({
        userId: indicator?.user_id,
        userName: indicator?.user?.full_name,
        isTyping: indicator?.is_typing,
        updatedAt: indicator?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error getting typing status:', error);
      throw error;
    }
  },

  /**
   * Subscribe to typing indicator updates
   */
  subscribeToTypingIndicators(negotiationId, callback) {
    const channel = supabase?.channel(`typing:${negotiationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'typing_indicators',
          filter: `negotiation_id=eq.${negotiationId}`
        },
        async (payload) => {
          // Get current typing status and call callback
          const typingStatus = await this.getTypingStatus(negotiationId);
          callback(typingStatus);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Subscribe to real-time message updates
   */
  subscribeToMessages(negotiationId, callback) {
    const channel = supabase?.channel(`messages:${negotiationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'negotiation_messages',
          filter: `negotiation_id=eq.${negotiationId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribeFromMessages(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Unsubscribe from typing indicator updates
   */
  unsubscribeFromTypingIndicators(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Quick action templates
   */
  quickActions: {
    accept: {
      text: 'Prihvatam vašu ponudu',
      type: 'system'
    },
    reject: {
      text: 'Odbijam ponudu',
      type: 'system'
    },
    counter: {
      text: 'Imam protuponudu',
      type: 'offer'
    },
    requestInfo: {
      text: 'Molim za dodatne informacije',
      type: 'text'
    }
  }
};